import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, X, Check } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

/**
 * Customer picker that lets the user pick an existing customer OR
 * add a brand-new one inline without leaving the current dialog.
 *
 * Props:
 *  - customers: [{id,name,company}]
 *  - value: selected customer id
 *  - onChange(id)
 *  - onCustomersChange(newCustomersArray) — parent should refresh its list
 */
export default function CustomerPicker({ customers, value, onChange, onCustomersChange }) {
  const [mode, setMode] = useState("select"); // 'select' | 'new'
  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "" });
  const [saving, setSaving] = useState(false);

  const emailClash = form.email.trim()
    ? customers.find((c) => (c.email || "").trim().toLowerCase() === form.email.trim().toLowerCase())
    : null;

  const createNew = async () => {
    if (!form.name.trim()) { toast.error("Customer name is required"); return; }
    setSaving(true);
    try {
      const { data: created } = await api.post("/customers", form);
      const { data: refreshed } = await api.get("/customers");
      onCustomersChange?.(refreshed);
      onChange(created.id);
      toast.success(`Added ${created.name}`);
      setMode("select");
      setForm({ name: "", company: "", email: "", phone: "" });
    } catch { toast.error("Failed to add customer"); }
    finally { setSaving(false); }
  };

  if (mode === "new") {
    return (
      <div className="border hairline p-3 space-y-2" data-testid="customer-picker-new">
        <div className="flex items-center justify-between">
          <span className="overline">New Customer</span>
          <button className="text-slate-400 hover:text-slate-900" onClick={() => setMode("select")} data-testid="customer-picker-cancel">
            <X size={16} />
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <div>
            <Label className="text-xs text-slate-600">Name *</Label>
            <Input data-testid="picker-new-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div>
            <Label className="text-xs text-slate-600">Company</Label>
            <Input data-testid="picker-new-company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} />
          </div>
          <div>
            <Label className="text-xs text-slate-600">Phone</Label>
            <Input data-testid="picker-new-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          </div>
          <div>
            <Label className="text-xs text-slate-600">Email</Label>
            <Input data-testid="picker-new-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </div>
        </div>
        {emailClash && (
          <div className="text-xs px-3 py-2 rounded-md" style={{ background: "#FEF5E6", color: "#B36B00", border: "1px solid #FCE0B3" }} data-testid="picker-email-dup-warning">
            Heads up: another customer <b>{emailClash.name}</b> already uses this email. Saving will create a <b>separate</b> customer with the same email.
          </div>
        )}
        <div className="flex justify-end">
          <button className="btn-primary text-sm flex items-center gap-1" onClick={createNew} disabled={saving} data-testid="picker-new-save">
            <Check size={14} /> {saving ? "Adding…" : "Add & Select"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2">
        <div className="flex-1">
          <Select value={value} onValueChange={onChange}>
            <SelectTrigger data-testid="customer-picker-select">
              <SelectValue placeholder="Select customer" />
            </SelectTrigger>
            <SelectContent>
              {customers.length === 0 && (
                <div className="p-2 text-xs text-slate-500">No customers yet — add one below.</div>
              )}
              {customers.map((c) => (
                <SelectItem key={c.id} value={c.id}>{c.name}{c.company ? ` — ${c.company}` : ""}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <button
          type="button"
          className="btn-ghost text-sm whitespace-nowrap flex items-center gap-1"
          onClick={() => setMode("new")}
          data-testid="customer-picker-add-new"
        >
          <Plus size={14} /> New
        </button>
      </div>
    </div>
  );
}
